import type { DropdownFilterCallback } from '../../components/Dropdown/Dropdown';
import type { ContentTypeFormProperty } from './helpers/parseProperty';

import { use, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { getContentType } from '../../lib/flotiq-client';
import { getCtdsFromQuery } from '../../lib/flotiq-client/api-helpers';
import {
  parseResponseErrors,
  ResponseError,
} from '../../lib/flotiq-client/response-errors';
import { EMPTY_ARRAY } from '../../lib/globals';

import { useContentTypes } from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import useSelectedSpace from '../../hooks/useSelectedSpace';
import useToken from '../../hooks/useToken';

import { ModalInstanceContext, useModals } from '../../contexts/ModalContext';
import PropertyModalCtdsContext from '../../contexts/PropertyModalCtdsContext';

import { generateZodSchema } from './helpers/validators';
import PropertyModalFormContent from './PropertyModalFormContent/PropertyModalFormContent';

const CTD_PARAMS = {
  limit: 20,
  order_by: 'label',
  order_direction: 'asc',
};

interface PropertyModalFormProps {
  /**
   * Property object to edit
   */
  property?: ContentTypeFormProperty;
  /**
   * Order of current content type
   */
  order?: string[];
  /**
   * If property is new
   */
  isNew?: boolean;
  /**
   * If property is duplication
   */
  isDuplicate?: boolean;
  /**
   * Name of the parent field
   */
  parentInputType?: string;
  /**
   * Test id for property modal form
   */
  testId?: string;
}

const PropertyModalForm = ({
  property,
  order = EMPTY_ARRAY,
  isNew = false,
  isDuplicate = false,
  testId = '',
  parentInputType,
}: PropertyModalFormProps) => {
  const jwt = useToken();
  const { space } = useSelectedSpace();
  const { t } = useTranslation();
  const modal = useModals();
  const modalInstance = use(ModalInstanceContext);

  const {
    data: ctds,
    errors: ctdsErrors,
    isLoading: ctdsAreLoading,
  } = useContentTypes(CTD_PARAMS);

  const finalCtdOptions = useMemo(() => {
    const options = ctds
      .filter((ctd) => !ctd.internal || ['_media', '_tag'].includes(ctd.name))
      .map((ctd) => ({
        label: ctd.label,
        value: ctd.name,
      }));
    options.unshift({
      label: t('ContentTypeForm.Descriptions.AnyType'),
      value: '',
    });
    return options;
  }, [ctds, t]);

  useApiErrorsToast(ctdsErrors);

  const getValidationType = useCallback(
    async (contentTypeName: string) => {
      try {
        const { body, status, ok } = await getContentType(jwt, space, {
          contentTypeName,
        });
        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        return body;
      } catch (error) {
        toast.error(
          t(
            error instanceof ResponseError
              ? 'ContentForm.CouldntFetch'
              : 'Form.CommunicationErrorMessage',
          ),
        );
        return null;
      }
    },
    [jwt, space, t],
  );

  const filterCtds = useCallback<DropdownFilterCallback>(
    async (query, _, setIsLoading) => {
      setIsLoading(true);

      const newCtds = (
        await getCtdsFromQuery(jwt, space, { ...CTD_PARAMS, label: query }, t)
      )
        .filter((ctd) => !ctd.internal || ['_media', '_tag'].includes(ctd.name))
        .map((ctd) => ({ label: ctd.label, value: ctd.name }));

      setIsLoading(false);

      if (
        t('ContentTypeForm.Descriptions.AnyType')
          .toLowerCase()
          .replace(/\s+/g, '')
          .includes(query.toLowerCase().replace(/\s+/g, ''))
      ) {
        newCtds.unshift({
          label: t('ContentTypeForm.Descriptions.AnyType'),
          value: '',
        });
      }
      return newCtds;
    },
    [jwt, space, t],
  );

  const propertyCtdsContextValue = useMemo(
    () => ({
      ctds: finalCtdOptions,
      getValidationType,
      ctdsAreLoading,
      filterCtds,
    }),
    [finalCtdOptions, ctdsAreLoading, filterCtds, getValidationType],
  );

  const handleSubmit = useCallback(
    async ({ value }: { value: ContentTypeFormProperty }) => {
      if (property && property.key !== value.key && !isNew) {
        const result = await modal.confirmation(
          t('PropertyForm.ChangeKeyConfirm'),
        );
        if (!result) return;
      }

      modalInstance.resolve?.(value);
    },
    [property, isNew, modalInstance, modal, t],
  );

  const validationSchema = useMemo(
    () => generateZodSchema(t, isDuplicate, property, order),
    [t, property, order, isDuplicate],
  );

  return (
    <PropertyModalCtdsContext value={propertyCtdsContextValue}>
      <PropertyModalFormContent
        onSubmit={handleSubmit}
        property={property}
        validationSchema={validationSchema}
        parentInputType={parentInputType}
        testId={testId}
      />
    </PropertyModalCtdsContext>
  );
};

export default PropertyModalForm;
